import type { FeatureCollection } from "geojson";
import * as linkify from "linkifyjs";
import React, { useCallback } from "react";
import { ageOptions, sentimentOptions } from "./constants/constants";
import i18n from "./i18n";

export const pipe =
  (...fns) =>
  (x) =>
    fns.reduce((v, f) => f(v), x);

/*
https://arizonatribe.github.io/vanillas/global.html#pick

Removes everything except the specified keys from an object (after cloning the Object).

@param keys 	Array.<String> 	 An array of keys to search for in the Object and include from the output
@param obj 	  Object 	         An Object from which to copy and remove keys

@returns A copy of the original Object, but with only the specified keys
*/
export const pick = (keys, obj) => {
  const newObj = {};
  const numOfKeys = keys.length;
  for (let i = 0; i < numOfKeys; i++) {
    if (keys[i] in obj) {
      newObj[keys[i]] = obj[keys[i]];
    }
  }
  return newObj;
};

export const openLink = (weblink: string) => {
  const convertedLinkRaw = weblink && linkify.find(weblink);
  const convertedLink =
    weblink && convertedLinkRaw[0] !== undefined && convertedLinkRaw[0].href;

  window.open(convertedLink, "_blank");
};
export const openMail = (contact: string, subject?: string, body?: string) => {
  if (contact && subject && body) {
    openLink(`mailto:${contact}?subject=${subject}&body=${body}`);
  } else if (contact && subject) {
    openLink(`mailto:${contact}?subject=${subject}`);
  } else if (contact) {
    openLink(`mailto:${contact}`);
  }
};

export const todayIsWithinDateRange = (startDate: Date, endDate: Date) => {
  if (!startDate || !endDate) {
    return false;
  }
  const today = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Set end date's time to the last possible moment of the day (23:59:59.999)
  end.setHours(23, 59, 59, 999);

  // Remove time component from the current date to compare only the dates
  start.setHours(0, 0, 0, 0);
  return today >= start && today <= end;
};

export const getPhaseOfDateRange = (
  startDate: string | Date,
  endDate: string | Date,
) => {
  if (!startDate || !endDate) {
    return "notYetScheduled";
  }

  const today = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Set end date's time to the last possible moment of the day (23:59:59.999)
  end.setHours(23, 59, 59, 999);
  start.setHours(0, 0, 0, 0);

  // Remove time component from the current date to compare only the dates
  today.setHours(0, 0, 0, 0);

  if (today < start) {
    return "before";
  }
  if (today > end) {
    return "after";
  }
  return "while";
};

export const useTraceUpdate = (props) => {
  const prev = React.useRef(props);
  React.useEffect(() => {
    const changedProps = Object.entries(props).reduce((ps, [k, v]) => {
      if (prev.current[k] !== v) {
        ps[k] = [prev.current[k], v];
      }
      return ps;
    }, {});
    if (Object.keys(changedProps).length > 0) {
      console.log("Changed props:", changedProps);
    }
    prev.current = props;
  });
};

export const generateLink = (app: string, link?: string) => {
  const env = import.meta.env.VITE_APP_MODE;
  const base = app === "monitoring" ? "dashboard.senf" : "senf";
  const port = app === "monitoring" ? "3001" : "3000";
  const url =
    env === "development"
      ? `http://localhost:${port}/`
      : env === "production"
        ? `https://${base}.app/`
        : `https://demo.${base}.app/`;

  return `${url}${link || ""}`;
};
export function truncateString(str: string, num: number) {
  if (!str) return str;
  if (str.length > num) {
    return `${str.substring(0, num)}...`;
  }
  return str;
}

// export const generateHash = (string) => {
//   let hash = 0;
//   if (string.length == 0) return hash;
//   for (let i = 0; i < string.length; i++) {
//     const charCode = string.charCodeAt(i);
//     hash = (hash << 7) - hash + charCode;
//     hash &= hash;
//   }
//   return hash;
// };

export const getNumericId = (uid: string) => {
  // Take first 8 chars and convert to numeric value
  return Number.parseInt(uid.slice(0, 8), 36).toString().slice(0, 6);
};
export const getUserHandle = (
  isAnonymous: boolean,
  uid: string,
  userHandle: string,
) => {
  return uid && (isAnonymous || !userHandle)
    ? `${i18n.t("global:guest")}-${getNumericId(uid)}`
    : userHandle;
};

export function generateHash(input) {
  const str = String(input);
  let hash = 0;
  if (str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    const charCode = str.charCodeAt(i);
    hash = (hash << 5) - hash + charCode;
    hash &= hash;
  }
  return hash;
}

export function unhash(hash) {
  // Implement a hash function that converts the hash back to its original state
  // For example, you can use the implementation of the hash function that you provided earlier:
  const string = hash.toString();
  let hashVal = 0;
  for (let i = 0; i < string.length; i++) {
    const char = string.charCodeAt(i);
    hashVal = (hashVal << 5) - hashVal + char;
    hashVal &= hashVal;
  }
  return hashVal;
}

export function debounce(func, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
}

export function topicsArrayToObject(topics) {
  return topics.reduce((acc, { value, label, color }) => {
    return { ...acc, [value]: { label, color } };
  }, {});
}
export function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function formatDateBasedOnLanguage(date: Date, language: string) {
  const convertedDate = new Date(date);
  if (isNaN(convertedDate.getTime())) {
    console.error("Invalid date provided");
    return;
  }

  const dateOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  };

  // Use the provided language parameter directly.
  // Most environments will default to English (or the environment's default locale)
  // if the specified language/locale is not supported.
  try {
    return convertedDate.toLocaleDateString(language, dateOptions);
  } catch (e) {
    console.error(
      `Locale '${language}' not supported. Falling back to default.`,
    );
    // You can specify a default locale here if desired, or simply return undefined or an error message.
    return convertedDate.toLocaleDateString("en-US", dateOptions); // Fallback to English (US)
  }
}

export function addModeToSessionStorage() {
  const searchParams = new URLSearchParams(window.location.search);
  const mode = searchParams.get("mode");

  if (mode) {
    sessionStorage.setItem("mode", mode);
  }
}

export function getModeFromSessionStorage() {
  const mode = sessionStorage.getItem("mode");
  return mode;
}

/**
 * Converts a number of bytes to a human-readable file size string with a specified number of decimal places.
 *
 * @param {number} bytes - The number of bytes to convert.
 * @param {number} [decimals=2] - The number of decimal places to display in the result (default is 2).
 * @returns {string} The formatted file size string.
 *
 * @note According to JEDEC standartization 1kB = 1000 Bytes, 1KB = 1024 Bytes. 1 KiB is also 1024 Bytes but not used here because it is far less common.
 */
export function formatBytes(
  bytes: number,
  options?: {
    decimals?: number;
    si?: boolean;
  },
): string {
  const { decimals = 2, si = false } = options || {};
  if (!bytes) return "0 Bytes";

  const k = si ? 1000 : 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Number.parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}
// Helper function for delaying execution
export const delay = (ms: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

export const isURL = (str: string) => {
  // Regular expression to match URL pattern
  const urlPattern =
    /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(:\d{1,5})?([/?#]\S*)?$/i;

  return urlPattern.test(str);
};

export const setFocus = (
  index: number,
  select = false,
  active = document.activeElement as HTMLInputElement,
) => {
  if (!active) return;

  // Get all the input elements in the document
  const inputElements = document.querySelectorAll(
    "input, select, textarea, button, [tabindex]",
  );

  // Find the index of the currently focused input element
  const currentIndex = Array.from(inputElements).findIndex(
    (el) => el === active,
  );

  // Find the next input element
  let nextElement = inputElements[currentIndex + index] as HTMLInputElement;
  while (nextElement && nextElement.disabled) {
    nextElement = inputElements[
      currentIndex + index + index
    ] as HTMLInputElement;
  }

  // Focus on the next input element
  if (nextElement) {
    nextElement.focus();
    if (select && typeof nextElement.select === "function")
      nextElement.select();
  } else {
    active.blur();
  }
};

export function parseGeoData<T extends FeatureCollection>(
  geoData?: string | null | Record<string, any>,
): T {
  return typeof geoData === "string"
    ? JSON.parse(geoData)
    : !geoData
      ? null
      : geoData;
}

export const getInitialsColor = (
  inputText,
  type = "tailwind",
  usage = "bg",
) => {
  // Special case for "other"
  if (inputText === "other") {
    return type === "tailwind" ? `${usage}-category-surface-yellow` : "#e1ad35";
  }

  let text;
  // Check if the text is a number and if so convert it to its corresponding letter
  if (typeof inputText === "number") {
    text =
      inputText === -1
        ? "e"
        : String.fromCharCode(65 + Number.parseInt(inputText, 10));
  } else {
    // Get the last character of the input text, or 'e' if empty/undefined
    text = inputText?.slice(-1) || "e";
  }

  const colors =
    type === "tailwind"
      ? [
          `${usage}-category-surface-rose`,
          `${usage}-category-surface-green`,
          `${usage}-category-surface-teal`,
          `${usage}-category-surface-blue`,
          `${usage}-category-surface-purple`,
          `${usage}-category-surface-red`,
          `${usage}-category-surface-orange`,
          `${usage}-category-surface-brown`,
          `${usage}-tag-text-green`,
          `${usage}-tag-text-teal`,
          `${usage}-tag-text-blue`,
          `${usage}-tag-text-purple`,
          `${usage}-tag-text-rose`,
          `${usage}-tag-text-red`,
          `${usage}-tag-text-orange`,
          `${usage}-tag-text-brown`,
        ]
      : [
          "#e67eab",
          "#5ab15f",
          "#3dadbb",
          "#809af9",
          "#b588ff",
          "#f07e67",
          "#e4892d",
          "#cc9365",
          "#265e2a",
          "#0a5b65",
          "#354ca1",
          "#60438f",
          "#883559",
          "#92321f",
          "#7e4308",
          "#724926",
        ];

  const colorIndex = text.toLowerCase().charCodeAt(0) % colors.length;

  return colors[colorIndex];
};

/**
 * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
 *
 * @param text The text to be rendered.
 * @param {String} font The css font descriptor that text is to be rendered with (e.g. "14px verdana").
 *
 * @see http://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
 */
export function getTextWidth(text: string, font: string): number {
  // Define a type-safe cache for the canvas
  if (!getTextWidth.canvas) {
    getTextWidth.canvas = document.createElement("canvas");
  }

  const context = getTextWidth.canvas.getContext("2d");
  if (!context) {
    console.error("Failed to get 2D context");
    return (
      text.length * Number.parseFloat(font.split(" ")[0].replace("px", ""))
    );
  }

  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
}

export const getLongestLabel = <T extends Record<string, any>>(
  array: T[],
  getter: (item: T) => string | undefined,
  options: { fontSize?: string; padding?: number; minWidth?: number } = {},
) => {
  const {
    fontSize = "normal 14px Nunito",
    padding = 28,
    minWidth = 72,
  } = options;
  return array
    .flatMap((e) => {
      const label = getter(e) || "";
      return label.split(" ");
    })
    .reduce((acc, e) => {
      return Math.max(acc, getTextWidth(e, fontSize) + padding);
    }, minWidth);
};

export namespace getTextWidth {
  export let canvas: HTMLCanvasElement | undefined;
}
/**
 * Generates an alphabetic representation for a given numeric input.
 *
 * This function converts a numeric input into a corresponding alphabetic representation
 * based on the position in the English alphabet. For example, 1 is converted to 'A',
 * 2 to 'B', 27 to 'AA', and so on.
 *
 */
export const getAlpha = (i: number): string => {
  const num = i + 1;
  const mod = num % 26 || 26;
  const pow = Math.floor(num / 27);
  const out = String.fromCharCode(64 + mod);

  if (pow && i < 999) return getAlpha(pow - 1) + out;
  return out;
};

export function getSentimentName(sentiment) {
  // if (!age) {
  //   return "noInformation";
  // }
  for (const option of sentimentOptions) {
    if (sentiment >= option.lowerBound && sentiment <= option.upperBound) {
      return option.value;
    }
  }
}

export function getAgeGroupName(age) {
  if (!age) {
    return "noInformation";
  }
  for (const option of ageOptions) {
    if (age >= option.lowerBound && age <= option.upperBound) {
      return option.value;
    }
  }
}

export const getAgeGroup = (age: number) => {
  if (age < 18) {
    return "<18";
  }
  if (age >= 18 && age <= 24) {
    return "18-24";
  }
  if (age >= 25 && age <= 34) {
    return "25-34";
  }
  if (age >= 35 && age <= 44) {
    return "35-44";
  }
  if (age >= 45 && age <= 54) {
    return "45-54";
  }
  if (age >= 55 && age <= 64) {
    return "55-64";
  }
  if (age >= 65 && age <= 74) {
    return "65-74";
  }
  if (age >= 75) {
    return "75+";
  }
  return "";
};

export function calculateSubmissionWeek(createdAt, startDate, endDate, t) {
  // Convert the date strings to Date objects
  const createdAtDate = new Date(createdAt);
  const startDateDate = new Date(startDate);
  const endDateDate = new Date(endDate);

  // Check if the createdAt date is within the specified range
  if (createdAtDate >= startDateDate && createdAtDate <= endDateDate) {
    // Calculate the difference in milliseconds between createdAt and startDate
    const timeDifference = createdAtDate - startDateDate;

    // Calculate the number of weeks by dividing the time difference by the number of milliseconds in a week
    const weekNumber = Math.ceil(timeDifference / (7 * 24 * 60 * 60 * 1000));

    // Use the translation function to display the week number
    return `${t("global:week")} ${weekNumber}`;
  }
  // Return a message indicating that the createdAt date is outside the specified range
  return t("global:invalidDate");
}

export function createSafeFilename(
  parts: (string | undefined)[],
  maxLength = 250,
) {
  const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const normalizeAndSanitize = (str: string) =>
    str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9_]/g, "")
      .replace(/ /g, "_");
  const sanitizedParts = parts.map((part) => {
    return part && normalizeAndSanitize(part);
  });
  sanitizedParts.push(currentDate);
  const filteredParts = sanitizedParts.filter((e) => typeof e === "string");
  const maxPartLength = Math.floor(maxLength / filteredParts.length);

  return filteredParts
    .map((e) => {
      if (e.length > maxPartLength)
        return `${e.slice(0, maxPartLength - 3)}...`;
      return e;
    })
    .join("_");
}

export function fixFalslyTranslatedMarkdown(text) {
  if (typeof text !== "string") {
    return text;
  }

  let fixedMarkdown = text;

  // Remove <u> and </u> tags, preserving the content between them
  fixedMarkdown = fixedMarkdown.replace(/<u>|<\/u>/gi, "");

  // Make > possible
  fixedMarkdown = fixedMarkdown.replace(/>/g, "&gt;");

  // Replace all occurrences of '&#x20;' with nothing
  fixedMarkdown = fixedMarkdown.replace(/&#x20;/g, "");

  // Replace all occurrences of '** ' with '**'
  // fixedMarkdown = fixedMarkdown.replace(/(^|\n)\*\* /g, "$1**");

  // Replace all occurrences of ' **' with '**'
  // fixedMarkdown = fixedMarkdown.replace(/ \*\*(?=\W|$)/g, "**");

  // Replace ';' with '**' if it is at the end of a line
  // fixedMarkdown = fixedMarkdown.replace(/;(\r?\n)/g, "**$1");
  // Handle unclosed bold markers '**'
  fixedMarkdown = fixedMarkdown.replace(
    /(\*\*[\s\S]*?)(?:(\*\*)|(\r?\n|$))/g,
    (match, p1, p2, p3) => {
      if (p2 === "**") {
        return match; // If it ends with '**', return as is
      }
      return `${p1}**${p3 || ""}`; // If it does not end with '**', add it and maintain line breaks
    },
  );

  return fixedMarkdown;
}

export const getAverage = (arr: number[]) => {
  if (arr.length === 0) return 0;
  const sum = arr.reduce((acc, curr) => acc + curr, 0);
  return sum / arr.length;
};

export const getMedian = (arr: number[]) => {
  if (arr.length === 0) return 0;
  const sorted = arr.sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};

export const round = (num: number, decimals = 2) => {
  return Math.round(num * 10 ** decimals) / 10 ** decimals;
};

export type DeepComparable =
  | string
  | number
  | boolean
  | null
  | undefined
  | DeepComparable[]
  | { [key: string]: DeepComparable };

/**
 * Deep equality comparison function to replace lodash's isEqual
 * Performs a deep comparison between two values to determine if they are equivalent.
 * @param a First value to compare
 * @param b Second value to compare
 * @returns boolean indicating if the values are deeply equal
 */
export function isEqual(a: DeepComparable, b: DeepComparable): boolean {
  // Handle simple cases first
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a !== "object") return a === b;

  // Handle arrays
  if (Array.isArray(a)) {
    if (!Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    return a.every((item, index) => isEqual(item, b[index]));
  }
  if (Array.isArray(b)) return false;

  // Handle objects
  const objA = a as Record<string, DeepComparable>;
  const objB = b as Record<string, DeepComparable>;

  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) return false;

  return keysA.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(objB, key) &&
      isEqual(objA[key], objB[key]),
  );
}

export const getFileTypeLabel = (
  n = 1,
  fileTypes?: string[],
  explicit?: { singular: string; plural: string },
) => {
  if (explicit) {
    return n === 1 ? explicit.singular : explicit.plural;
  }
  if (!fileTypes) {
    return n === 1 ? "global:file" : "global:files";
  }
  if (fileTypes.every((type) => type.startsWith("image/"))) {
    return n === 1 ? "global:image" : "global:images";
  }
  if (fileTypes.every((type) => type.startsWith("video/"))) {
    return n === 1 ? "global:video" : "global:videos";
  }
  return n === 1 ? "global:file" : "global:files";
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export const stripMarkdown = (text: string | undefined): string => {
  if (!text) return "";
  return text
    .replace(/\*\*(.*?)\*\*/g, "$1") // bold
    .replace(/\*(.*?)\*/g, "$1") // italic
    .replace(/\*(?=\s|$)/g, "") // standalone asterisks
    .replace(/\*(?=\w)/g, "") // asterisks before words
    .replace(/(\w)\*/g, "$1") // asterisks after words
    .replace(/\[(.*?)\]\((.*?)\)/g, "$1") // links
    .replace(/#{1,6}\s/g, "") // headers
    .replace(/>\s(.*)/g, "$1") // blockquotes
    .replace(/\n/g, " ") // newlines
    .replace(/\s+/g, " ") // multiple spaces
    .trim();
};

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export function diffObjects<T extends object | null>(obj1: T, obj2: T): any {
  if (Object.is(obj1, obj2)) {
    return undefined;
  }

  // Handle arrays by iterating over the maximum length.
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    const maxLen = Math.max(obj1.length, obj2.length);
    const result: (T | null)[] = [];
    let changed = false;

    for (let i = 0; i < maxLen; i++) {
      const diffResult = diffObjects(obj1[i], obj2[i]);
      if (diffResult === undefined) {
        result[i] = null; // Unchanged items set to null
      } else {
        result[i] = diffResult;
        changed = true;
      }
    }
    return changed ? result : undefined;
  }

  // If one is an array and the other is not, or one is not an object, return the new value.
  if (
    Array.isArray(obj1) ||
    Array.isArray(obj2) ||
    typeof obj1 !== "object" ||
    typeof obj2 !== "object" ||
    obj1 === null ||
    obj2 === null
  ) {
    return obj2;
  }

  // Handle objects by comparing all keys.
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const result: Record<string, any> = {};
  const keys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
  let changed = false;

  for (const key of keys) {
    if (!(key in obj2)) {
      // Property was removed in obj2, explicitly set it to null
      result[key] = undefined;
      changed = true;
    } else {
      // INSERT_YOUR_REWRITE_HERE
      const diffResult = diffObjects(
        (obj1 as Record<string, object | null>)[key],
        (obj2 as Record<string, object | null>)[key],
      );
      if (diffResult !== undefined) {
        result[key] = diffResult;
        changed = true;
      }
    }
  }

  return changed ? result : undefined;
}
